import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Building2, Calendar, Clock, Edit, Users, MapPin, FileText, ClipboardList, Camera, Plus, ImageIcon, Euro, AlertTriangle, Package, Receipt, HardHat, PackageCheck, TrendingUp, TrendingDown } from "lucide-react";
import { ObraEntregaTab } from "./ObraEntregaTab";
import { ObraEntregaDialog } from "./ObraEntregaDialog";
import { useObrasContext } from "@/contexts/ObrasContext";
import { useAuth } from "@/contexts/AuthContext";
import { ObraForm } from "./ObraForm";
import { ObraOTsTab } from "./ObraOTsTab";
import { ObraMaterialesTab } from "./ObraMaterialesTab";
import { ObraGastosTab } from "./ObraGastosTab";
import { JornadaForm } from "@/components/jornadas/JornadaForm";
import { JornadaDocumentos } from "@/components/jornadas/JornadaDocumentos";
import { JornadaDetalleCardDialog, type JornadaDetalle } from "@/components/jornadas/JornadaDetalleCardDialog";
import { AsignarCosteJornadaDialog } from "./AsignarCosteJornadaDialog";
import { TarifaTecnicoDialog } from "@/components/usuarios/TarifaTecnicoDialog";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { getWorkTypeLabel, getWorkTypeColor } from "@/lib/workTypeConstants";
import { useIsMobile } from "@/hooks/use-mobile";

interface ObraDetailProps {
  obraId: string;
  isOpen: boolean;
  onClose: () => void;
}

export function ObraDetail({ obraId, isOpen, onClose }: ObraDetailProps) {
  const { getObraById, getJornadasByObra, obras, refreshJornadas } = useObrasContext();
  const { perfil } = useAuth();
  const isMobile = useIsMobile();
  const [showEditForm, setShowEditForm] = useState(false);
  const [showJornadaForm, setShowJornadaForm] = useState(false);
  const [fotos, setFotos] = useState<Array<{ id: string; jornada_id: string; url_archivo: string; nombre_archivo: string; created_at: string; tecnico?: string; fecha?: string }>>([]);
  const [fotosCount, setFotosCount] = useState<Record<string, number>>({});
  const [jornadaFotosOpen, setJornadaFotosOpen] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [costeBreakdown, setCosteBreakdown] = useState<Array<{ tecnico_id: string; tecnico_nombre: string; horas: number; coste: number; horas_sin_coste: number }>>([]);
  const [costeJornadaTarget, setCosteJornadaTarget] = useState<any | null>(null);
  const [tarifaTecnicoTarget, setTarifaTecnicoTarget] = useState<{ id: string; nombre: string } | null>(null);
  const [costePorTipo, setCostePorTipo] = useState<{ materiales: number; gastos: number; albaranes_pendientes: number }>({ materiales: 0, gastos: 0, albaranes_pendientes: 0 });
  const [entregaDialogOpen, setEntregaDialogOpen] = useState(false);
  const [entregaDocsCount, setEntregaDocsCount] = useState<number | null>(null);
  const [prevEstado, setPrevEstado] = useState<string | null>(null);
  const [pendienteValidar, setPendienteValidar] = useState<{ coste: number; horas: number }>({ coste: 0, horas: 0 });
  const [jornadaDetalleOpen, setJornadaDetalleOpen] = useState<JornadaDetalle | null>(null);

  const obra = getObraById(obraId);
  const jornadas = getJornadasByObra(obraId);
  const isAdmin = perfil?.rol === 'admin' || perfil?.rol === 'gestor';

  // Cargar fotos de las jornadas de la obra
  useEffect(() => {
    const loadFotos = async () => {
      if (!jornadas.length) {
        setFotos([]);
        setFotosCount({});
        return;
      }
      const ids = jornadas.map(j => j.id);
      const { data, error } = await supabase
        .from('jornada_documentos')
        .select('id, jornada_id, url_archivo, nombre_archivo, created_at, tipo_documento')
        .in('jornada_id', ids)
        .order('created_at', { ascending: false });
      if (error || !data) return;
      const onlyImages = data.filter(d =>
        d.tipo_documento === 'foto' ||
        /\.(jpe?g|png|webp|gif|heic)$/i.test(d.nombre_archivo)
      );
      const enriched = await Promise.all(onlyImages.map(async f => {
        const j = jornadas.find(jj => jj.id === f.jornada_id);
        let signedUrl = f.url_archivo;
        // If url is a path (not full http) or matches storage URL, sign it
        const path = f.url_archivo?.startsWith('http')
          ? (f.url_archivo.match(/\/jornada-documentos\/(.+?)(\?|$)/)?.[1] ?? null)
          : f.url_archivo;
        if (path) {
          const { data: signed } = await supabase.storage
            .from('jornada-documentos')
            .createSignedUrl(path, 3600);
          if (signed?.signedUrl) signedUrl = signed.signedUrl;
        }
        return { ...f, url_archivo: signedUrl, tecnico: j?.tecnico?.nombre, fecha: j?.fecha };
      }));
      setFotos(enriched);
      const counts: Record<string, number> = {};
      onlyImages.forEach(f => {
        counts[f.jornada_id] = (counts[f.jornada_id] || 0) + 1;
      });
      setFotosCount(counts);
    };
    if (isOpen) loadFotos();
  }, [isOpen, jornadas.length, obraId]);

  // Cargar desglose de coste por técnico (jornadas directas + jornadas vía OT + partes vía OT)
  useEffect(() => {
    if (!isOpen || !isAdmin) return;
    const load = async () => {
      // 1) Obtener IDs de OTs vinculadas a la obra
      const { data: ots } = await supabase
        .from('ordenes_trabajo')
        .select('id')
        .eq('obra_id', obraId);
      const otIds = (ots || []).map(o => o.id);

      // 2) Jornadas que pertenecen a la obra (directas o vía OT)
      let jornadasQuery = supabase
        .from('obra_jornadas')
        .select('tecnico_id, horas_trabajadas, coste_total, coste_hora_aplicado, estado_validacion, tecnico:perfiles(nombre)')
        .or(`obra_id.eq.${obraId}${otIds.length ? `,orden_trabajo_id.in.(${otIds.join(',')})` : ''}`);
      const { data: jornadasData } = await jornadasQuery;

      // 3) Partes vinculados a OTs de la obra
      let partesData: any[] = [];
      if (otIds.length > 0) {
        const { data } = await supabase
          .from('partes_trabajo')
          .select('tecnico_id, duracion_total, coste_total, coste_hora_aplicado')
          .in('orden_trabajo_id', otIds);
        partesData = data || [];
      }

      // Sumar pendientes (no aprobadas) para el indicador
      let pendCoste = 0;
      let pendHoras = 0;
      (jornadasData || []).forEach((j: any) => {
        if (j.estado_validacion !== 'aprobada') {
          pendCoste += Number(j.coste_total) || 0;
          pendHoras += Number(j.horas_trabajadas) || 0;
        }
      });
      setPendienteValidar({ coste: pendCoste, horas: pendHoras });

      // Agrupar por técnico (solo aprobadas para el desglose auditado)
      const byTec = new Map<string, { nombre: string; horas: number; coste: number; horas_sin_coste: number }>();
      const add = (tecId: string, nombre: string, horas: number, coste: number | null) => {
        const cur = byTec.get(tecId) || { nombre, horas: 0, coste: 0, horas_sin_coste: 0 };
        cur.horas += horas;
        if (coste != null) {
          cur.coste += coste;
        } else if (horas > 0) {
          cur.horas_sin_coste += horas;
        }
        byTec.set(tecId, cur);
      };

      (jornadasData || []).forEach((j: any) => {
        if (j.estado_validacion !== 'aprobada') return;
        const horas = Number(j.horas_trabajadas) || 0;
        const coste = j.coste_total != null ? Number(j.coste_total) : null;
        const nombre = j.tecnico?.nombre || 'Técnico';
        if (j.tecnico_id) add(j.tecnico_id, nombre, horas, coste);
      });
      partesData.forEach((p: any) => {
        // duracion_total viene como interval string "HH:MM:SS"
        let h = 0;
        if (typeof p.duracion_total === 'string') {
          const m = p.duracion_total.match(/^(\d+):(\d+):(\d+)/);
          if (m) h = Number(m[1]) + Number(m[2]) / 60 + Number(m[3]) / 3600;
        }
        const coste = p.coste_total != null ? Number(p.coste_total) : null;
        const existing = byTec.get(p.tecnico_id);
        const nombre = existing?.nombre || 'Técnico';
        if (p.tecnico_id) add(p.tecnico_id, nombre, h, coste);
      });

      const arr = Array.from(byTec.entries())
        .map(([tecnico_id, v]) => ({
          tecnico_id,
          tecnico_nombre: v.nombre,
          horas: v.horas,
          coste: v.coste,
          horas_sin_coste: v.horas_sin_coste,
        }))
        .sort((a, b) => b.coste - a.coste);
      setCosteBreakdown(arr);
    };
    load();
  }, [isOpen, isAdmin, obraId, jornadas.length]);

  // Cargar subtotales de materiales y otros gastos
  useEffect(() => {
    if (!isOpen || !isAdmin) return;
    const loadTipos = async () => {
      const [{ data: recs }, { data: gastos }] = await Promise.all([
        supabase.from('recepciones_material').select('importe_total').eq('obra_id', obraId),
        supabase.from('obra_gastos').select('importe').eq('obra_id', obraId),
      ]);
      const materiales = (recs || []).reduce((s: number, r: any) => s + (Number(r.importe_total) || 0), 0);
      const pendientes = (recs || []).filter((r: any) => r.importe_total == null).length;
      const gastosTotal = (gastos || []).reduce((s: number, g: any) => s + (Number(g.importe) || 0), 0);
      setCostePorTipo({ materiales, gastos: gastosTotal, albaranes_pendientes: pendientes });
    };
    loadTipos();
  }, [isOpen, isAdmin, obraId, obra?.coste_consumido]);

  // Cargar conteo de documentos de entrega + abrir diálogo si se acaba de finalizar
  useEffect(() => {
    if (!isOpen) return;
    const loadCount = async () => {
      const { count } = await (supabase as any)
        .from('obra_entrega_documentos')
        .select('id', { count: 'exact', head: true })
        .eq('obra_id', obraId);
      setEntregaDocsCount(count ?? 0);
    };
    loadCount();
  }, [isOpen, obraId, entregaDialogOpen]);

  useEffect(() => {
    if (!isOpen || !obra) return;
    if (prevEstado && prevEstado !== 'finalizada' && obra.estado === 'finalizada' && isAdmin) {
      // Reset entrega_pospuesta y abrir diálogo
      (supabase as any).from('obras').update({ entrega_pospuesta: false }).eq('id', obraId).then(() => {});
      setEntregaDialogOpen(true);
    }
    setPrevEstado(obra.estado);
  }, [obra?.estado, isOpen, isAdmin, obraId, prevEstado]);

  if (!obra) return null;

  const getEstadoBadgeVariant = (estado: string) => {
    switch (estado) {
      case 'activa': return 'default';
      case 'pausada': return 'secondary';
      case 'finalizada': return 'outline';
      case 'cancelada': return 'destructive';
      default: return 'default';
    }
  };

  const getProgressValue = () => {
    if (obra.horas_estimadas === 0) return 0;
    return Math.min((obra.horas_consumidas / obra.horas_estimadas) * 100, 100);
  };

  const getProgressColor = () => {
    const progress = getProgressValue();
    if (progress >= 100) return "text-destructive";
    if (progress >= 80) return "text-warning";
    return "text-primary";
  };

  const totalJornadas = jornadas.length;
  const horasTrabajadasHoy = jornadas
    .filter(j => j.fecha === format(new Date(), 'yyyy-MM-dd'))
    .reduce((sum, j) => sum + (j.horas_trabajadas || 0), 0);

  if (showEditForm) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="w-[96vw] max-w-4xl max-h-[92vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>Editar Obra</DialogTitle>
          </DialogHeader>
          <ObraForm 
            obra={obra} 
            onSuccess={() => {
              setShowEditForm(false);
              onClose();
            }} 
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[96vw] max-w-6xl max-h-[92vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pr-10 sm:pr-12">
          <div className="space-y-1 min-w-0">
            {obra.numero_obra && (
              <p className="text-xs font-mono text-muted-foreground">{obra.numero_obra}</p>
            )}
            {obra.numero_presupuesto && (
              <p className="text-xs font-mono text-muted-foreground">Presupuesto: {obra.numero_presupuesto}</p>
            )}
            <DialogTitle className="text-xl sm:text-2xl flex items-center gap-2">
              <Building2 className="h-5 w-5 sm:h-6 sm:w-6 shrink-0" />
              <span className="truncate">{obra.nombre}</span>
            </DialogTitle>
            <div className="flex items-center gap-3 sm:gap-4 text-sm text-muted-foreground flex-wrap">
              <span className="flex items-center gap-1 min-w-0">
                <Users className="h-4 w-4 shrink-0" />
                <span className="truncate">{obra.cliente?.nombre}</span>
              </span>
              <Badge variant={getEstadoBadgeVariant(obra.estado)}>
                {obra.estado}
              </Badge>
              {obra.estado === 'finalizada' && entregaDocsCount === 0 && (
                <Badge variant="destructive" className="cursor-pointer" onClick={() => isAdmin && setEntregaDialogOpen(true)}>
                  <PackageCheck className="h-3 w-3 mr-1" />
                  Documentación de entrega pendiente
                </Badge>
              )}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {obra.estado === 'activa' && (
              <Button variant="default" onClick={() => setShowJornadaForm(true)} className="flex-1 sm:flex-none">
                <Plus className="h-4 w-4 mr-2" />
                Registrar jornada
              </Button>
            )}
            {isAdmin && (
              <Button variant="outline" onClick={() => setShowEditForm(true)} className="flex-1 sm:flex-none">
                <Edit className="h-4 w-4 mr-2" />
                Editar
              </Button>
            )}
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Progress Overview */}
          <div className={`grid grid-cols-2 gap-3 sm:gap-4 ${isAdmin ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
            {isAdmin && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Horas Estimadas</CardTitle>
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{obra.horas_estimadas}h</div>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Horas Consumidas</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${getProgressColor()}`}>
                  {obra.horas_consumidas}h
                </div>
              </CardContent>
            </Card>

            {isAdmin && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Progreso</CardTitle>
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className={`text-2xl font-bold ${getProgressColor()}`}>
                    {getProgressValue().toFixed(1)}%
                  </div>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Jornadas</CardTitle>
                <Calendar className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{totalJornadas}</div>
              </CardContent>
            </Card>
          </div>

          {/* Progress Bar - Only for Admins */}
          {isAdmin && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Progreso de la Obra</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Completado</span>
                    <span>{obra.horas_consumidas}h / {obra.horas_estimadas}h</span>
                  </div>
                  <Progress value={getProgressValue()} className="h-3" />
                  {getProgressValue() > 100 && (
                    <p className="text-sm text-destructive">
                      ⚠️ Esta obra ha excedido las horas estimadas
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Evolución económica (modo fácil basado en precio de venta) */}
          {isAdmin && obra.precio_venta != null && Number(obra.precio_venta) > 0 && (() => {
            const venta = Number(obra.precio_venta);
            const coste = Number(obra.coste_consumido) || 0;
            const margen = venta - coste;
            const margenPct = (margen / venta) * 100;
            const consumoPct = (coste / venta) * 100;
            const fmt = (v: number) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(v);
            const barColor =
              consumoPct > 100 ? 'bg-destructive'
              : consumoPct >= 90 ? 'bg-destructive/80'
              : consumoPct >= 70 ? 'bg-amber-500'
              : 'bg-emerald-500';
            const margenColor = margen < 0 ? 'text-destructive' : 'text-emerald-600';
            return (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    {consumoPct > 100 ? <TrendingDown className="h-5 w-5 text-destructive" /> : <TrendingUp className="h-5 w-5 text-emerald-600" />}
                    Evolución económica
                  </CardTitle>
                  <CardDescription>
                    Seguimiento en vivo del margen según el precio de venta y el coste consumido.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="rounded-md border bg-muted/40 p-3">
                      <div className="text-xs text-muted-foreground">Precio de venta</div>
                      <div className="text-2xl font-bold tabular-nums">{fmt(venta)}</div>
                    </div>
                    <div className="rounded-md border bg-muted/40 p-3">
                      <div className="text-xs text-muted-foreground">Coste consumido</div>
                      <div className="text-2xl font-bold tabular-nums">{fmt(coste)}</div>
                      <div className="text-[11px] text-muted-foreground mt-1">{consumoPct.toFixed(0)}% de la venta</div>
                    </div>
                    <div className="rounded-md border bg-muted/40 p-3">
                      <div className="text-xs text-muted-foreground">{margen >= 0 ? 'Margen restante' : 'Pérdida'}</div>
                      <div className={`text-2xl font-bold tabular-nums ${margenColor}`}>
                        {margen >= 0 ? '' : '-'}{fmt(Math.abs(margen))}
                      </div>
                      <div className={`text-[11px] mt-1 ${margenColor}`}>{margenPct >= 0 ? '+' : ''}{margenPct.toFixed(0)}%</div>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="h-3 w-full rounded-full bg-muted overflow-hidden">
                      <div
                        className={`h-full ${barColor} transition-all`}
                        style={{ width: `${Math.min(consumoPct, 100)}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>{consumoPct.toFixed(1)}% consumido</span>
                      {consumoPct > 100 ? (
                        <span className="text-destructive font-medium">⚠️ Coste por encima de la venta</span>
                      ) : consumoPct >= 90 ? (
                        <span className="text-destructive">Margen casi agotado</span>
                      ) : consumoPct >= 70 ? (
                        <span className="text-amber-600">Vigilar el consumo</span>
                      ) : (
                        <span className="text-emerald-600">Margen saludable</span>
                      )}
                    </div>
                  </div>
                  {obra.coste_mano_obra_estimado > 0 && (
                    <div className="text-xs text-muted-foreground">
                      Presupuesto de mano de obra: {fmt(Number(obra.coste_mano_obra_estimado))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })()}

          {/* Coste mano de obra */}
          {isAdmin && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Euro className="h-5 w-5" />
                  Coste consumido
                </CardTitle>
                <CardDescription>
                  Mano de obra (jornadas y partes) + materiales valorados + gastos imputados.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <div className="text-3xl font-bold">
                    {new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(obra.coste_consumido || 0)}
                  </div>
                  {obra.coste_mano_obra_estimado > 0 && (
                    <div className="text-sm text-muted-foreground">
                      de {new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(obra.coste_mano_obra_estimado)} presupuestados
                    </div>
                  )}
                  {(pendienteValidar.coste > 0 || pendienteValidar.horas > 0) && (
                  <div className="text-xs text-amber-600 dark:text-amber-400 italic">
                    Pendiente de validar: {new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(pendienteValidar.coste)} y {pendienteValidar.horas.toFixed(1)}h
                  </div>
                  )}
                </div>
                {obra.coste_mano_obra_estimado > 0 && (() => {
                  const pct = Math.min((obra.coste_consumido / obra.coste_mano_obra_estimado) * 100, 100);
                  const realPct = (obra.coste_consumido / obra.coste_mano_obra_estimado) * 100;
                  return (
                    <div className="space-y-1">
                      <Progress value={pct} className="h-3" />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>{realPct.toFixed(1)}% del presupuesto</span>
                        {realPct > 100 && (
                          <span className="text-destructive">⚠️ Presupuesto excedido</span>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {(() => {
                  const manoObra = costeBreakdown.reduce((s, c) => s + (c.coste || 0), 0);
                  const materiales = costePorTipo.materiales;
                  const gastos = costePorTipo.gastos;
                  const total = manoObra + materiales + gastos;
                  const pct = (v: number) => total > 0 ? (v / total) * 100 : 0;
                  const fmt = (v: number) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(v);
                  const items = [
                    { key: 'mo', label: 'Mano de obra', value: manoObra, icon: HardHat, barClass: 'bg-primary' },
                    { key: 'mat', label: 'Materiales', value: materiales, icon: Package, barClass: 'bg-blue-500' },
                    { key: 'gas', label: 'Otros gastos', value: gastos, icon: Receipt, barClass: 'bg-amber-500' },
                  ];
                  return (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      {items.map(it => {
                        const Icon = it.icon;
                        const p = pct(it.value);
                        return (
                          <div key={it.key} className={`rounded-md border bg-muted/40 p-3 ${it.value === 0 ? 'opacity-60' : ''}`}>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                              <Icon className="h-3.5 w-3.5" />
                              <span>{it.label}</span>
                            </div>
                            <div className="text-lg font-semibold">{fmt(it.value)}</div>
                            <div className="mt-2 h-1.5 w-full rounded-full bg-muted overflow-hidden">
                              <div className={`h-full ${it.barClass} transition-all`} style={{ width: `${p}%` }} />
                            </div>
                            <div className="text-[11px] text-muted-foreground mt-1">{p.toFixed(0)}% del total</div>
                            {it.key === 'mat' && costePorTipo.albaranes_pendientes > 0 && (
                              <div className="text-[11px] text-amber-600 mt-1 inline-flex items-center gap-1">
                                <AlertTriangle className="h-3 w-3" />
                                {costePorTipo.albaranes_pendientes} albarán(es) sin valorar
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                {costeBreakdown.length > 0 ? (
                  <div className="space-y-2">
                    {costeBreakdown.map((c) => (
                      <div key={c.tecnico_id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between rounded-md border p-3 text-sm gap-2">
                        <div className="min-w-0">
                          <div className="font-medium">{c.tecnico_nombre}</div>
                          <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-2">
                            <span>{c.horas.toFixed(1)}h trabajadas</span>
                            {c.horas_sin_coste > 0 && (
                              <>
                                <span className="inline-flex items-center gap-1 text-amber-600">
                                  <AlertTriangle className="h-3 w-3" />
                                  {c.horas_sin_coste.toFixed(1)}h sin tarifa
                                </span>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-6 px-2 text-xs"
                                  onClick={() => setTarifaTecnicoTarget({ id: c.tecnico_id, nombre: c.tecnico_nombre })}
                                >
                                  Configurar tarifa
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="font-semibold whitespace-nowrap">
                          {new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(c.coste)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground italic">
                    Aún no hay jornadas imputadas a esta obra.
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          <Tabs defaultValue="info" className="w-full">
            <TabsList className="w-full md:grid md:grid-cols-8 flex overflow-x-auto justify-start md:justify-center">
              <TabsTrigger value="info" className="whitespace-nowrap">Información</TabsTrigger>
              <TabsTrigger value="ots" className="whitespace-nowrap">
                <ClipboardList className="h-4 w-4 mr-1" />
                OTs
              </TabsTrigger>
              <TabsTrigger value="jornadas" className="whitespace-nowrap">Jornadas</TabsTrigger>
              <TabsTrigger value="materiales" className="whitespace-nowrap">
                <Package className="h-4 w-4 mr-1" />
                Materiales
              </TabsTrigger>
              <TabsTrigger value="gastos" className="whitespace-nowrap">
                <Euro className="h-4 w-4 mr-1" />
                Gastos
              </TabsTrigger>
              <TabsTrigger value="fotos" className="whitespace-nowrap">
                <ImageIcon className="h-4 w-4 mr-1" />
                Fotos {fotos.length > 0 && <Badge variant="secondary" className="ml-1">{fotos.length}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="entrega" className="whitespace-nowrap">
                <PackageCheck className="h-4 w-4 mr-1" />
                Entrega
              </TabsTrigger>
              <TabsTrigger value="resumen" className="whitespace-nowrap">Resumen</TabsTrigger>
            </TabsList>

            <TabsContent value="info" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Detalles de la Obra</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-medium text-sm text-muted-foreground">Fecha de Inicio</h4>
                      <p className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {format(new Date(obra.fecha_inicio), "dd/MM/yyyy", { locale: es })}
                      </p>
                    </div>
                    
                    {obra.fecha_fin_estimada && (
                      <div>
                        <h4 className="font-medium text-sm text-muted-foreground">Fecha Fin Estimada</h4>
                        <p className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {format(new Date(obra.fecha_fin_estimada), "dd/MM/yyyy", { locale: es })}
                        </p>
                      </div>
                    )}
                    
                    <div>
                      <h4 className="font-medium text-sm text-muted-foreground">Cliente</h4>
                      <p className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        {obra.cliente?.nombre}
                      </p>
                    </div>
                    
                    <div>
                      <h4 className="font-medium text-sm text-muted-foreground">Estado</h4>
                      <Badge variant={getEstadoBadgeVariant(obra.estado)}>
                        {obra.estado}
                      </Badge>
                    </div>
                  </div>

                  {obra.descripcion && (
                    <div>
                      <h4 className="font-medium text-sm text-muted-foreground mb-2">Descripción</h4>
                      <p className="text-sm bg-muted p-3 rounded-md">
                        {obra.descripcion}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="ots">
              <ObraOTsTab obraId={obraId} />
            </TabsContent>

            <TabsContent value="jornadas">
              <Card>
                <CardHeader>
                  <CardTitle>Registro de Jornadas</CardTitle>
                  <CardDescription>
                    Historial de tiempo trabajado en esta obra
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {jornadas.length > 0 ? (
                    isMobile ? (
                      <div className="space-y-3">
                        {jornadas.map((jornada) => (
                          <div
                            key={jornada.id}
                            className="rounded-lg border p-3 space-y-2 bg-card/50 cursor-pointer hover:bg-accent/50 transition-colors"
                            onClick={() => setJornadaDetalleOpen({
                              id: jornada.id,
                              fecha: jornada.fecha,
                              hora_inicio: jornada.hora_inicio,
                              hora_fin: jornada.hora_fin ?? null,
                              horas_trabajadas: jornada.horas_trabajadas ?? null,
                              tipo_trabajo: jornada.tipo_trabajo,
                              descripcion_trabajo: jornada.descripcion_trabajo ?? null,
                              es_hora_extra: !!jornada.es_hora_extra,
                              estado_validacion: (jornada as any).estado_validacion || 'pendiente',
                              motivo_rechazo: (jornada as any).motivo_rechazo ?? null,
                              tecnico: jornada.tecnico ? { nombre: jornada.tecnico.nombre, email: '' } : null,
                              obra: obra ? { nombre: obra.nombre } : null,
                              orden_trabajo: null,
                              cliente: null,
                            })}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <div className="font-semibold text-sm truncate">{jornada.tecnico?.nombre}</div>

                                <div className="text-xs text-muted-foreground">
                                  {format(new Date(jornada.fecha), "dd/MM/yyyy", { locale: es })}
                                </div>
                              </div>
                              <Badge
                                variant="outline"
                                className="text-[10px] shrink-0"
                                style={{
                                  backgroundColor: getWorkTypeColor(jornada.tipo_trabajo) + '20',
                                  borderColor: getWorkTypeColor(jornada.tipo_trabajo),
                                  color: getWorkTypeColor(jornada.tipo_trabajo),
                                }}
                              >
                                {getWorkTypeLabel(jornada.tipo_trabajo)}
                              </Badge>
                            </div>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {jornada.hora_inicio} - {jornada.hora_fin || "En curso"}
                              </span>
                              <span className="font-medium">
                                {jornada.horas_trabajadas?.toFixed(2) || 0}h
                              </span>
                              {jornada.es_hora_extra && (
                                <Badge variant="outline" className="bg-orange-500/10 text-orange-600 border-orange-500/40 text-[10px]">
                                  Extra
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center justify-between gap-2 pt-1 border-t">
                              {isAdmin ? (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2 text-xs"
                                  onClick={(e) => { e.stopPropagation(); setCosteJornadaTarget({
                                    id: jornada.id,
                                    tecnico_id: jornada.tecnico_id,
                                    tecnico_nombre: jornada.tecnico?.nombre,
                                    fecha: jornada.fecha,
                                    horas_trabajadas: jornada.horas_trabajadas,
                                    es_hora_extra: jornada.es_hora_extra,
                                    coste_hora_aplicado: jornada.coste_hora_aplicado ?? null,
                                  }); }}
                                >
                                  {jornada.coste_total != null
                                    ? new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(Number(jornada.coste_total))
                                    : <span className="inline-flex items-center gap-1 text-amber-600"><AlertTriangle className="h-3 w-3" />Asignar coste</span>}
                                  <Edit className="h-3 w-3 ml-1 opacity-50" />
                                </Button>
                              ) : <span />}
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2 text-xs"
                                onClick={(e) => { e.stopPropagation(); setJornadaFotosOpen(jornada.id); }}
                              >
                                <Camera className="h-3.5 w-3.5 mr-1" />
                                {fotosCount[jornada.id] || 0}
                              </Button>
                            </div>
                            {jornada.descripcion_trabajo && (
                              <p className="text-xs text-muted-foreground line-clamp-2">
                                {jornada.descripcion_trabajo}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Fecha</TableHead>
                          <TableHead>Técnico</TableHead>
                          <TableHead>Tipo de Trabajo</TableHead>
                          <TableHead>Horario</TableHead>
                          <TableHead>Horas</TableHead>
                          {isAdmin && <TableHead className="text-right">Importe</TableHead>}
                          <TableHead>Fotos</TableHead>
                          <TableHead>Descripción</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {jornadas.map((jornada) => (
                          <TableRow
                            key={jornada.id}
                            className="cursor-pointer hover:bg-accent/50"
                            onClick={() => setJornadaDetalleOpen({
                              id: jornada.id,
                              fecha: jornada.fecha,
                              hora_inicio: jornada.hora_inicio,
                              hora_fin: jornada.hora_fin ?? null,
                              horas_trabajadas: jornada.horas_trabajadas ?? null,
                              tipo_trabajo: jornada.tipo_trabajo,
                              descripcion_trabajo: jornada.descripcion_trabajo ?? null,
                              es_hora_extra: !!jornada.es_hora_extra,
                              estado_validacion: (jornada as any).estado_validacion || 'pendiente',
                              motivo_rechazo: (jornada as any).motivo_rechazo ?? null,
                              tecnico: jornada.tecnico ? { nombre: jornada.tecnico.nombre, email: '' } : null,
                              obra: obra ? { nombre: obra.nombre } : null,
                              orden_trabajo: null,
                              cliente: null,
                            })}
                          >
                            <TableCell>
                              {format(new Date(jornada.fecha), "dd/MM/yyyy", { locale: es })}
                            </TableCell>
                            <TableCell>{jornada.tecnico?.nombre}</TableCell>
                            <TableCell>
                              <Badge 
                                variant="outline" 
                                style={{ 
                                  backgroundColor: getWorkTypeColor(jornada.tipo_trabajo) + '20',
                                  borderColor: getWorkTypeColor(jornada.tipo_trabajo),
                                  color: getWorkTypeColor(jornada.tipo_trabajo)
                                }}
                              >
                                {getWorkTypeLabel(jornada.tipo_trabajo)}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {jornada.hora_inicio} - {jornada.hora_fin || "En curso"}
                            </TableCell>
                            <TableCell>
                              {jornada.horas_trabajadas?.toFixed(2) || 0}h
                              {jornada.es_hora_extra && (
                                <Badge variant="outline" className="ml-2 bg-orange-500/10 text-orange-600 border-orange-500/40">
                                  Extra
                                </Badge>
                              )}
                            </TableCell>
                            {isAdmin && (
                              <TableCell className="text-right font-medium">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 px-2 font-medium"
                                  onClick={(e) => { e.stopPropagation(); setCosteJornadaTarget({
                                    id: jornada.id,
                                    tecnico_id: jornada.tecnico_id,
                                    tecnico_nombre: jornada.tecnico?.nombre,
                                    fecha: jornada.fecha,
                                    horas_trabajadas: jornada.horas_trabajadas,
                                    es_hora_extra: jornada.es_hora_extra,
                                    coste_hora_aplicado: jornada.coste_hora_aplicado ?? null,
                                  }); }}
                                >
                                  {jornada.coste_total != null
                                    ? new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(Number(jornada.coste_total))
                                    : <span className="inline-flex items-center gap-1 text-amber-600"><AlertTriangle className="h-3 w-3" />Asignar coste</span>}
                                  <Edit className="h-3 w-3 ml-1 opacity-50" />
                                </Button>
                              </TableCell>
                            )}
                            <TableCell>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 px-2"
                                onClick={(e) => { e.stopPropagation(); setJornadaFotosOpen(jornada.id); }}
                              >
                                <Camera className="h-4 w-4 mr-1" />
                                {fotosCount[jornada.id] || 0}
                              </Button>
                            </TableCell>
                            <TableCell className="max-w-xs truncate">
                              {jornada.descripcion_trabajo || "-"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    )
                  ) : (
                    <div className="text-center py-8">
                      <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-medium mb-2">Sin jornadas registradas</h3>
                      <p className="text-muted-foreground">
                        Aún no se han registrado jornadas de trabajo para esta obra
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="materiales">
              <ObraMaterialesTab obraId={obraId} />
            </TabsContent>

            <TabsContent value="gastos">
              <ObraGastosTab obraId={obraId} />
            </TabsContent>

            <TabsContent value="fotos">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ImageIcon className="h-5 w-5" />
                    Galería de Fotos
                  </CardTitle>
                  <CardDescription>
                    Todas las fotos subidas en las jornadas de esta obra
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {fotos.length === 0 ? (
                    <div className="text-center py-8">
                      <Camera className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-medium mb-2">Sin fotos</h3>
                      <p className="text-muted-foreground">
                        Las fotos que los técnicos suban a sus jornadas aparecerán aquí
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                      {fotos.map(f => (
                        <button
                          key={f.id}
                          type="button"
                          onClick={() => setLightbox(f.url_archivo)}
                          className="group relative aspect-square overflow-hidden rounded-lg border bg-muted hover:ring-2 hover:ring-primary transition-all"
                        >
                          <img
                            src={f.url_archivo}
                            alt={f.nombre_archivo}
                            loading="lazy"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          />
                          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 text-left">
                            <p className="text-[10px] text-white/90 leading-tight">
                              {f.fecha && format(new Date(f.fecha), "dd/MM/yyyy", { locale: es })}
                            </p>
                            {f.tecnico && (
                              <p className="text-[10px] text-white/70 truncate">{f.tecnico}</p>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="entrega">
              <ObraEntregaTab obraId={obraId} obraEstado={obra.estado} />
            </TabsContent>

            <TabsContent value="resumen">
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Resumen de Actividad</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="text-center p-4 bg-muted rounded-lg">
                        <h3 className="text-2xl font-bold">{totalJornadas}</h3>
                        <p className="text-sm text-muted-foreground">Jornadas Totales</p>
                      </div>
                      
                      <div className="text-center p-4 bg-muted rounded-lg">
                        <h3 className="text-2xl font-bold">{horasTrabajadasHoy.toFixed(1)}h</h3>
                        <p className="text-sm text-muted-foreground">Horas Hoy</p>
                      </div>
                      
                      <div className="text-center p-4 bg-muted rounded-lg">
                        <h3 className="text-2xl font-bold">
                          {jornadas.length > 0 ? 
                            (obra.horas_consumidas / jornadas.length).toFixed(1) : 
                            0
                          }h
                        </h3>
                        <p className="text-sm text-muted-foreground">Promedio por Jornada</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </DialogContent>

      {/* Registrar jornada desde la obra */}
      <Dialog open={showJornadaForm} onOpenChange={setShowJornadaForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Registrar Jornada</DialogTitle>
          </DialogHeader>
          <JornadaForm
            obrasActivas={obras.filter(o => o.estado === 'activa')}
            obraInfo={{ id: obra.id, nombre: obra.nombre, cliente_nombre: obra.cliente?.nombre }}
            onSuccess={() => {
              setShowJornadaForm(false);
              refreshJornadas();
            }}
            onCancel={() => setShowJornadaForm(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Fotos de una jornada concreta */}
      <Dialog open={!!jornadaFotosOpen} onOpenChange={(o) => !o && setJornadaFotosOpen(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Documentos de la jornada</DialogTitle>
          </DialogHeader>
          {jornadaFotosOpen && <JornadaDocumentos jornadaId={jornadaFotosOpen} />}
        </DialogContent>
      </Dialog>

      {/* Lightbox */}
      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-5xl p-0 bg-transparent border-0 shadow-none">
          {lightbox && (
            <img src={lightbox} alt="Foto" className="w-full h-auto rounded-lg" />
          )}
        </DialogContent>
      </Dialog>

      {/* Asignar coste manual a jornada */}
      <AsignarCosteJornadaDialog
        open={!!costeJornadaTarget}
        onOpenChange={(o) => !o && setCosteJornadaTarget(null)}
        jornada={costeJornadaTarget}
        onSaved={() => {
          refreshJornadas();
        }}
      />

      {/* Configurar tarifa del técnico */}
      {tarifaTecnicoTarget && (
        <TarifaTecnicoDialog
          open={!!tarifaTecnicoTarget}
          onOpenChange={(o) => !o && setTarifaTecnicoTarget(null)}
          tecnicoId={tarifaTecnicoTarget.id}
          tecnicoNombre={tarifaTecnicoTarget.nombre}
        />
      )}
      <ObraEntregaDialog
        obraId={obraId}
        obraEstado={obra.estado}
        open={entregaDialogOpen}
        onClose={() => setEntregaDialogOpen(false)}
      />
      <JornadaDetalleCardDialog
        open={!!jornadaDetalleOpen}
        onOpenChange={(o) => { if (!o) setJornadaDetalleOpen(null); }}
        jornadas={jornadaDetalleOpen ? [jornadaDetalleOpen] : []}
        canValidate={false}
        onAprobar={async () => {}}
        onRechazar={async () => {}}
      />
    </Dialog>
  );
}